import DOMPurify from 'isomorphic-dompurify'
import { Job } from '../../../types/JobPost'
import apolloClient from '../../../apollo-client'
import { GET_JOB_POSTS_BY_SLUG } from '../../../graphql/jobPost'
import { useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import classes from '../../../components/ui/applyCaption/ApplyCaption.module.scss'
import ReactLazyMap from '../../../components/map/ReactLazyMap'
import JobsSearch from '../../../components/jobs/JobsSearch'
import HeroImage from '../../../components/ui/image/HeroImage'
import ApplyCaption from '../../../components/ui/applyCaption/ApplyCaption'
import RelatedVideos from '../../../components/related-videos/RelatedVideos'
import GlobalContext from '../../../contexts/GlobalContext'
import LeadContext from '../../../contexts/LeadContext'
import LeadAcquisitionModal from '../../../components/modals/LeadAcquisitionModal'
import JobPostDetailHead from '../../../components/heads/JobPostDetailHead'
import { GetStaticProps } from 'next'
import { ListBlankLoadMask } from '../../../components/ui/ListBlankLoadMask'

export default function JobsDetail({ jobPost }: { jobPost: Job }) {
  const { loadScripts } = useContext(GlobalContext)
  const [showLeadAcquisition, setShowLeadAcquisition] = useState(false)
  const { setLeadFormStaticData } = useContext(LeadContext)
  const [pageRendered, setPageRendered] = useState(false)

  const iframeRegex = /<iframe>*(.*?)<\/iframe>/g

  const router = useRouter()

  useEffect(() => {
    setPageRendered(true)
  }, [])

  useEffect(() => {
    const load = async () => {
      loadScripts('jobView')
    }

    load()
  }, [loadScripts])

  if (router.isFallback) {
    return <ListBlankLoadMask />
  }

  const onApply = (job: Job) => {
    if (job.applyURL) {
      window.open(job.applyURL, '_blank')
      return
    }
    setLeadFormStaticData({
      jobPostId: job._id,
      clientId: job.clientId,
      campaignId: job.campaignId || job.defaultCampaign?._id,
      clientName: job.client.name,
      heroImage: job.image?.Location || job.client?.resources?.hero?.Location
    })
    setShowLeadAcquisition(true)
  }
  return (
    <>
      <JobPostDetailHead
        title={jobPost.title}
        overview={jobPost.overview}
        details={jobPost.details}
        client={jobPost.client}
        hiringArea={jobPost.hiringArea}
        imageUrl={
          jobPost?.hero?.Location || jobPost?.client?.resources?.hero?.Location
        }></JobPostDetailHead>
      <section className="max-w-[1200px] mx-auto text-[#444]">
        <HeroImage
          srcUrl={
            jobPost.image?.Location || jobPost.client?.resources?.hero?.Location
          }
          alt={`${jobPost.title} Image`}
          height={1200}
          width={2400}
        />

        <ApplyCaption
          trackingNumber={jobPost?.trackingNumbers[0]?.trackingNumber}
          applyNowTxt={jobPost?.client.applyNowTxt || undefined}
          backButtonTxt={'All Job Posts'}
          backButtonHref={'/truck-driving-jobs'}
          onApply={() => onApply(jobPost)}
        />
        <div className="md:px-[0px] px-[0.6rem] md:pt-[20px] pt-[0.5rem] pb-[0px]">
          <h1 className="text-[1.8rem] font-arial-black uppercase mb-2 max-[769px]:text-[18px]">
            {jobPost?.title}
          </h1>
          <h2 className="text-[1.2rem] mb-8 font-arial font-bold leading-tight">
            {jobPost?.overview}
          </h2>
          <div
            suppressHydrationWarning
            dangerouslySetInnerHTML={{
              __html: jobPost.details
                ? DOMPurify.sanitize(jobPost.details?.replace(iframeRegex, ''))
                : ''
            }}></div>

          <RelatedVideos md={4} text={jobPost.details} />

          <div className={classes.hiringMapJob}>
            <h3 className={classes.subtitle}>Job Post Hiring Map</h3>
            <ReactLazyMap
              allStatesCoordinates={jobPost?.allStatesCoordinates}
              hiringArea={jobPost?.hiringArea}></ReactLazyMap>
          </div>
          {jobPost?.includeCompanyInfo && (
            <div className={classes.clientInformation}>
              <h3 className={classes.subtitle}>
                {jobPost?.client?.name} company Information
              </h3>

              <div
                className={classes.body}
                suppressHydrationWarning
                dangerouslySetInnerHTML={{
                  __html: jobPost?.client?.body
                    ? DOMPurify.sanitize(
                        jobPost?.client?.body.replace(iframeRegex, '')
                      )
                    : ''
                }}></div>
            </div>
          )}

          <h3 className={classes.subtitle}>
            Search Other {jobPost.client?.name} Jobs
          </h3>
        </div>
      </section>
      {jobPost && pageRendered && (
        <JobsSearch
          clientId={jobPost?.clientId}
          excludeJobPostId={jobPost?._id}
        />
      )}

      {showLeadAcquisition && (
        <LeadAcquisitionModal
          open={showLeadAcquisition}
          handleClose={setShowLeadAcquisition}
        />
      )}
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug
  try {
    const { data } = await apolloClient.query<any>({
      query: GET_JOB_POSTS_BY_SLUG,
      variables: { slug }
    })

    const jobPost = data.jobPostBySlug
    if (!jobPost) {
      throw new Error(`Job Post not found by slug ${slug}`)
    }
    return {
      revalidate: 60 * 5,
      props: {
        jobPost
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`error while executin getStaticProps of Jobs ${slug}`, error)
    return {
      notFound: true
    }
  }
}
